<template>
  <div class="flex flex-col gap-6">
    <!-- About Us Section -->
    <h2 class="text-2xl flex items-center">
      About Us
      <Button
        class="inline-block scale-75"
        variant="text"
        @click="back(3)"
      >
        <i class="fa fa-pen"></i>
      </Button>
    </h2>
    <div class="flex flex-col gap-1">
      <label class="text-xs">About Us</label>
      <p
        class="mb-4"
        data-test="about-us"
      >
        {{ companyAdminStore.aboutUsForm.states.aboutUs.value }}
      </p>

      <label class="text-xs">Custom URL</label>
      <p
        class="mb-4"
        data-test="custom-url"
      >
        {{ companyAdminStore.aboutUsForm.states.customUrl.value || '&mdash;' }}
      </p>

      <label class="text-xs">&ldquo;About Us&rdquo; Image</label>
      <p
        class="mb-4"
        v-if="companyAdminStore.aboutUsForm.states.aboutUsImage?.value"
      >
        <img
          :src="companyAdminStore.aboutUsForm.states.aboutUsImage?.value"
          alt="About Us Image"
          class="w-48 h-30 object-cover h-auto rounded-lg shadow-md"
          data-test="about-us-image"
        />
      </p>
      <p
        class="mb-4"
        v-else
      >
        No image provided
      </p>
    </div>

    <!-- Gives Day Information Section -->
    <h2 class="text-2xl flex items-center">
      Gives Day Information
      <Button
        class="inline-block scale-75"
        variant="text"
        @click="back(2)"
      >
        <i class="fa fa-pen"></i>
      </Button>
    </h2>
    <div class="flex flex-col gap-1">
      <label class="text-xs">Goal</label>
      <p
        class="mb-4"
        data-test="company-goal"
      >
        ${{ parseFloat(companyAdminStore.givesDayForm.states.companyGoal.value).toFixed(2) }}
      </p>
      <label class="text-xs">Participation Text</label>
      <p
        class="mb-4"
        data-test="participation-text"
      >
        {{ companyAdminStore.givesDayForm.states.participationText.value || '&mdash;' }}
      </p>

      <label class="text-xs">Employee Giving</label>
      <p class="mb-4">
        {{ verificationTypeLabel
        }}<span v-if="companyAdminStore.employeeGivingForm.states.verificationCompanyCode.value">
          ({{ companyAdminStore.employeeGivingForm.states.verificationCompanyCode.value }})
        </span>
      </p>
    </div>

    <!-- Featured Causes Section -->
    <h2 class="text-2xl flex items-center">
      Featured Causes
      <Button
        class="inline-block scale-75"
        variant="text"
        @click="back(1)"
      >
        <i class="fa fa-pen"></i>
      </Button>
    </h2>
    <div class="flex flex-col gap-1">
      <label class="text-xs">Featured Causes Text</label>
      <p
        class="mb-4"
        data-test="featured-causes-text"
      >
        {{ companyAdminStore.featuredCausesForm.states.featuredCausesText.value }}
      </p>

      <label class="text-xs">Organizations</label>
      <div
        v-if="companyAdminStore.featuredCausesForm.states.featuredOrganizations.value?.length"
        class="flex flex-col gap-2 mb-4"
      >
        <div
          v-for="organizations_id in companyAdminStore.featuredCausesForm.states.featuredOrganizations.value"
          :key="organizations_id"
          class="flex items-center gap-4 p-3 bg-white shadow-md rounded-lg border border-gray-200"
          data-test="organization"
        >
          <img
            :src="fundraiserStore.orgLogoUrl(organizations_id)"
            alt="Organization Logo"
            class="w-10 h-10 rounded-full object-cover"
          />
          <div class="text-gray-700 font-semibold flex grow">
            {{ organizations.find((org) => org.organizations_id === organizations_id)?.organizationName }}
          </div>
        </div>
      </div>
      <p
        class="mb-4"
        v-else
      >
        No organizations selected
      </p>

      <label class="text-xs">Media</label>
      <div
        class="mb-4 flex flex-wrap gap-2"
        v-if="companyAdminStore.mediaForm.states.images?.value"
      >
        <div
          v-for="img in companyAdminStore.mediaForm.states.images.value"
          :key="img"
        >
          <img
            :src="img"
            alt="Media Image"
            class="w-48 h-30 object-cover rounded-lg shadow-md flex"
            data-test="media-image"
          />
        </div>
      </div>
      <p
        class="mb-4"
        v-else
      >
        No media images provided
      </p>
    </div>
  </div>
</template>

<script setup>
import { defineEmits, onBeforeMount, ref, computed } from 'vue';
import { useCompanyAdminStore } from '@/stores/CompanyAdminStore.js';
import { useFundraiserStore } from '@/stores/FundraiserStore.js';
import { companyGivingVerificationType } from '@/helpers/enum.js';

const companyAdminStore = useCompanyAdminStore();
const fundraiserStore = useFundraiserStore();

const organizations = ref([]);

const emit = defineEmits(['back']);

const back = (step) => {
  emit('back', step);
};

const verificationTypeLabel = computed(() => {
  switch (companyAdminStore.employeeGivingForm.states.verificationType.value) {
    case companyGivingVerificationType.EMPLOYEE_ID:
      return 'Employee ID';
    case companyGivingVerificationType.COMPANY_CODE:
      return 'Company Code';
    default:
      return 'None Required';
  }
});

onBeforeMount(async () => {
  organizations.value = fundraiserStore.organizations;
});
</script>
